import {
  type ColumnDef,
  type ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowUpDown, ChevronLeft, Filter, Search, TrendingDown, TrendingUp } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DeltaCell, KpiTile, PillGroup } from "@/components/ci/overview-cells";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTimezone } from "@/contexts/timezone-context";
import { useCiOverview, useCiTrends } from "@/hooks/use-ci";
import { usePageTitle } from "@/hooks/use-page-title";
import { TEST_TYPE_FILTERS } from "@/lib/ci-filters";
import type { CiOverviewRow } from "@/lib/types";
import { cn, computeDelta, formatMetricValue } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const METRICS = [
  { label: "Total Restore", value: "total_restore_ms", unit: "ms", cryodOnly: false },
  { label: "CRIU Restore", value: "criu_restore_ms", unit: "ms", cryodOnly: false },
  { label: "Cold Start", value: "cold_start_ms", unit: "ms", cryodOnly: false },
  { label: "Inference", value: "inference_ms", unit: "ms", cryodOnly: false },
  { label: "HTTP Ready", value: "http_ready_ms", unit: "ms", cryodOnly: false },
  { label: "CUDA Restore", value: "phase_cuda_restore_s", unit: "s", cryodOnly: false },
  { label: "Checkpoint Size", value: "checkpoint_size_bytes", unit: "bytes", cryodOnly: false },
  {
    label: "Checkpoint Weights",
    value: "checkpoint_weights_bytes",
    unit: "bytes",
    cryodOnly: true,
  },
  { label: "K8s Overhead", value: "kubernetes_overhead_ms", unit: "ms", cryodOnly: true },
  { label: "Mooncake Transfer", value: "mooncake_transfer_ms", unit: "ms", cryodOnly: true },
  { label: "CRIU Binary", value: "criu_binary_duration_ms", unit: "ms", cryodOnly: true },
] as const;

const OVERVIEW_WINDOWS = [
  { label: "Latest", value: "latest" },
  { label: "7d", value: "7d" },
  { label: "30d", value: "30d" },
] as const;

const DETAIL_RANGES = [
  { label: "7d", value: "7d" },
  { label: "30d", value: "30d" },
  { label: "90d", value: "90d" },
] as const;

const AGGREGATIONS = [
  { label: "Mean", value: "mean" },
  { label: "Min", value: "min" },
  { label: "Max", value: "max" },
] as const;

const CHART_COLORS = [
  "oklch(0.55 0.15 250)",
  "oklch(0.65 0.15 85)",
  "oklch(0.55 0.15 300)",
  "oklch(0.45 0.12 160)",
  "oklch(0.6 0.15 20)",
];

const GPU_BADGE_STYLES: Record<string, string> = {
  h100: "bg-violet-500/10 text-violet-600 border-violet-500/20 dark:text-violet-400",
  h200: "bg-blue-500/10 text-blue-600 border-blue-500/20 dark:text-blue-400",
  a100: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 dark:text-emerald-400",
  l4: "bg-amber-500/10 text-amber-600 border-amber-500/20 dark:text-amber-400",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseTestDisplay(testName: string): { display: string; operation: string } {
  if (testName.startsWith("checkpoint-restore-")) {
    return { display: testName.slice(19), operation: "CR" };
  }
  if (testName.startsWith("cross-restore-")) {
    return { display: testName.slice(14), operation: "XR" };
  }
  return { display: testName, operation: "" };
}

function sanitizeKey(version: string): string {
  return version.replaceAll(".", "_");
}

function mergeVersionData(
  rawData: Array<{
    timestamp: string;
    value: number | null;
    run_id: string;
    version: string;
  }>,
): { merged: Array<Record<string, number | string | null>>; versions: string[] } {
  const versions = [...new Set(rawData.map((point) => point.version))].sort();
  const byTimestamp = new Map<number, Record<string, number | string | null>>();

  for (const point of rawData) {
    if (point.value == null) continue;
    const epochMs = new Date(point.timestamp).getTime();
    if (!byTimestamp.has(epochMs)) {
      const row: Record<string, number | string | null> = {
        timestamp: epochMs,
        run_id: point.run_id,
      };
      for (const version of versions) row[sanitizeKey(version)] = null;
      byTimestamp.set(epochMs, row);
    }
    const existing = byTimestamp.get(epochMs);
    if (existing) {
      existing[sanitizeKey(point.version)] = point.value;
    }
  }

  const merged = [...byTimestamp.values()].sort(
    (first, second) => (first.timestamp as number) - (second.timestamp as number),
  );
  return { merged, versions };
}

// ---------------------------------------------------------------------------
// Group overview rows → one row per test_name for the table
// ---------------------------------------------------------------------------

interface TestSummary {
  testName: string;
  displayName: string;
  operation: string;
  gpuType: string;
  primaryVersion: string;
  value: number | null;
  prevValue: number | null;
  delta: number | null;
  sampleCount: number;
  latestTimestamp: string;
  modelId: string;
  tensorParallel: number;
  tags: string[];
}

function groupOverviewRows(rows: CiOverviewRow[]): TestSummary[] {
  const grouped = new Map<string, CiOverviewRow[]>();
  for (const row of rows) {
    const existing = grouped.get(row.test_name) ?? [];
    existing.push(row);
    grouped.set(row.test_name, existing);
  }

  const summaries: TestSummary[] = [];
  for (const [testName, versionRows] of grouped) {
    const sorted = [...versionRows].sort(
      (first, second) =>
        new Date(second.latest_timestamp).getTime() - new Date(first.latest_timestamp).getTime(),
    );
    if (sorted.length === 0) continue;
    // biome-ignore lint/style/noNonNullAssertion: guarded by length check
    const primary = sorted[0]!;
    const { display, operation } = parseTestDisplay(testName);
    const value = primary.value;
    const prevValue = primary.prev_value;

    summaries.push({
      testName,
      displayName: display,
      operation,
      gpuType: primary.gpu_type || "unknown",
      primaryVersion: primary.version,
      value,
      prevValue,
      delta: computeDelta(value, prevValue),
      sampleCount: versionRows.reduce((sum, row) => sum + row.sample_count, 0),
      latestTimestamp: primary.latest_timestamp,
      modelId: primary.model_id || "",
      tensorParallel: primary.tensor_parallel || 0,
      tags: (primary.tags || "").split(",").filter(Boolean),
    });
  }

  return summaries;
}

// ---------------------------------------------------------------------------
// Reusable small components
// ---------------------------------------------------------------------------

function GpuBadge({ gpuType }: { gpuType: string }) {
  const key = gpuType.toLowerCase();
  const style = GPU_BADGE_STYLES[key] ?? "bg-muted text-muted-foreground border-border";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded border px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase leading-none tracking-wider",
        style,
      )}
    >
      {gpuType || "?"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Overview: TanStack Table with sorting + filtering
// ---------------------------------------------------------------------------

function OverviewTable({
  data,
  metricLabel,
  metricUnit,
  isLoading,
  onSelectTest,
}: {
  data: CiOverviewRow[];
  metricLabel: string;
  metricUnit: string;
  isLoading: boolean;
  onSelectTest: (testName: string) => void;
}) {
  const summaries = useMemo(() => groupOverviewRows(data), [data]);
  const gpuTypes = useMemo(
    () => [...new Set(summaries.map((summary) => summary.gpuType))].sort(),
    [summaries],
  );

  const [sorting, setSorting] = useState<SortingState>([{ id: "value", desc: true }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [gpuFilter, setGpuFilter] = useState<Set<string>>(new Set());

  const filteredSummaries = useMemo(() => {
    if (gpuFilter.size === 0) return summaries;
    return summaries.filter((summary) => gpuFilter.has(summary.gpuType));
  }, [summaries, gpuFilter]);

  const columns = useMemo<ColumnDef<TestSummary>[]>(
    () => [
      {
        accessorKey: "displayName",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 h-8 text-xs"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Model
            <ArrowUpDown className="ml-1 h-3 w-3" />
          </Button>
        ),
        cell: ({ row }) => {
          const { displayName, operation, tensorParallel, tags } = row.original;
          const hasDataParallel = tags.includes("dp");
          const hasExpertParallel = tags.includes("ep");
          const hasQuantization = tags.includes("awq") || tags.includes("fp8");
          const quantLabel = tags.includes("awq") ? "AWQ" : tags.includes("fp8") ? "FP8" : null;
          const hasMtp = tags.includes("mtp");
          return (
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium">{displayName}</span>
              {operation === "XR" && (
                <span className="rounded border border-amber-500/20 bg-amber-500/10 px-1 py-px font-mono text-[9px] font-semibold leading-none text-amber-600 dark:text-amber-400">
                  XR
                </span>
              )}
              {tensorParallel > 1 && (
                <span className="rounded border border-sky-500/20 bg-sky-500/10 px-1 py-px font-mono text-[9px] font-semibold leading-none text-sky-600 dark:text-sky-400">
                  TP{tensorParallel}
                </span>
              )}
              {hasDataParallel && (
                <span className="rounded border border-indigo-500/20 bg-indigo-500/10 px-1 py-px font-mono text-[9px] font-semibold leading-none text-indigo-600 dark:text-indigo-400">
                  DP
                </span>
              )}
              {hasExpertParallel && (
                <span className="rounded border border-fuchsia-500/20 bg-fuchsia-500/10 px-1 py-px font-mono text-[9px] font-semibold leading-none text-fuchsia-600 dark:text-fuchsia-400">
                  EP
                </span>
              )}
              {hasQuantization && quantLabel && (
                <span className="rounded border border-orange-500/20 bg-orange-500/10 px-1 py-px font-mono text-[9px] font-semibold leading-none text-orange-600 dark:text-orange-400">
                  {quantLabel}
                </span>
              )}
              {hasMtp && (
                <span className="rounded border border-teal-500/20 bg-teal-500/10 px-1 py-px font-mono text-[9px] font-semibold leading-none text-teal-600 dark:text-teal-400">
                  MTP
                </span>
              )}
            </div>
          );
        },
        filterFn: "includesString",
      },
      {
        accessorKey: "gpuType",
        header: "GPU",
        cell: ({ row }) => <GpuBadge gpuType={row.original.gpuType} />,
        enableSorting: false,
        size: 72,
      },
      {
        accessorKey: "primaryVersion",
        header: "Version",
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.primaryVersion}
          </span>
        ),
        size: 100,
      },
      {
        accessorKey: "value",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            className="-mr-2 ml-auto h-8 text-xs"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {metricLabel}
            <ArrowUpDown className="ml-1 h-3 w-3" />
          </Button>
        ),
        cell: ({ row }) => (
          <span className="font-mono text-sm font-medium tabular-nums">
            {formatMetricValue(row.original.value, metricUnit)}
          </span>
        ),
        sortingFn: (rowA, rowB) => {
          const valueA = rowA.original.value ?? 0;
          const valueB = rowB.original.value ?? 0;
          return valueA - valueB;
        },
        meta: { align: "right" },
        size: 120,
      },
      {
        accessorKey: "delta",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            className="-mr-2 ml-auto h-8 text-xs"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Delta
            <ArrowUpDown className="ml-1 h-3 w-3" />
          </Button>
        ),
        cell: ({ row }) => <DeltaCell delta={row.original.delta} />,
        sortingFn: (rowA, rowB) => {
          const deltaA = rowA.original.delta ?? 0;
          const deltaB = rowB.original.delta ?? 0;
          return deltaA - deltaB;
        },
        meta: { align: "right" },
        size: 90,
      },
      {
        accessorKey: "sampleCount",
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            className="-mr-2 ml-auto h-8 text-xs"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Runs
            <ArrowUpDown className="ml-1 h-3 w-3" />
          </Button>
        ),
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground tabular-nums">
            {row.original.sampleCount}
          </span>
        ),
        meta: { align: "right" },
        size: 70,
      },
    ],
    [metricLabel, metricUnit],
  );

  const table = useReactTable({
    data: filteredSummaries,
    columns,
    state: { sorting, columnFilters },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const toggleGpuFilter = useCallback((gpu: string) => {
    setGpuFilter((previous) => {
      const next = new Set(previous);
      if (next.has(gpu)) {
        next.delete(gpu);
      } else {
        next.add(gpu);
      }
      return next;
    });
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 8 }).map((_, index) => (
          <Skeleton key={`skeleton-${index}`} className="h-10 w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (summaries.length === 0) {
    return (
      <Card>
        <CardContent className="flex h-40 items-center justify-center">
          <span className="text-sm text-muted-foreground">
            No data — ClickHouse may be unavailable or no CI runs exist yet
          </span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {/* Toolbar: search + GPU filter */}
      <div className="flex items-center gap-2">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Filter models..."
            value={(table.getColumn("displayName")?.getFilterValue() as string) ?? ""}
            onChange={(event) => table.getColumn("displayName")?.setFilterValue(event.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-background px-3 text-xs font-medium shadow-xs hover:bg-accent hover:text-accent-foreground">
            <Filter className="h-3 w-3" />
            GPU
            {gpuFilter.size > 0 && (
              <span className="rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
                {gpuFilter.size}
              </span>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {gpuTypes.map((gpu) => (
              <DropdownMenuCheckboxItem
                key={gpu}
                checked={gpuFilter.has(gpu)}
                onCheckedChange={() => toggleGpuFilter(gpu)}
                className="font-mono text-xs uppercase"
              >
                {gpu || "unknown"}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {table.getFilteredRowModel().rows.length} of {summaries.length} tests
        </span>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => {
                  const align = (header.column.columnDef.meta as { align?: string })?.align;
                  return (
                    <TableHead
                      key={header.id}
                      className={cn("h-9", align === "right" && "text-right")}
                      style={{ width: header.column.columnDef.size }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  className="group cursor-pointer"
                  onClick={() => onSelectTest(row.original.testName)}
                >
                  {row.getVisibleCells().map((cell) => {
                    const align = (cell.column.columnDef.meta as { align?: string })?.align;
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn("py-2.5", align === "right" && "text-right")}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-20 text-center text-sm">
                  No matching tests
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary Cards
// ---------------------------------------------------------------------------

function OverviewSummaryCards({
  summaries,
  metricUnit,
}: {
  summaries: TestSummary[];
  metricUnit: string;
}) {
  const totalTests = summaries.length;
  const values = summaries
    .map((summary) => summary.value)
    .filter((value): value is number => value != null);

  const improving = summaries.filter(
    (summary) => summary.delta != null && summary.delta < -1,
  ).length;

  const regressing = summaries.filter(
    (summary) => summary.delta != null && summary.delta > 1,
  ).length;

  const medianValue =
    values.length > 0
      ? [...values].sort((first, second) => first - second)[Math.floor(values.length / 2)]
      : null;

  return (
    <div className="grid grid-cols-4 gap-3">
      <KpiTile label="Tests" value={totalTests} />
      <KpiTile label="Median" value={formatMetricValue(medianValue, metricUnit)} />
      <KpiTile
        label="Improving"
        icon={<TrendingDown className="h-3 w-3 text-emerald-500" />}
        value={improving}
        tone="text-emerald-600 dark:text-emerald-400"
      />
      <KpiTile
        label="Regressing"
        icon={<TrendingUp className="h-3 w-3 text-rose-500" />}
        value={regressing}
        tone="text-rose-600 dark:text-rose-400"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trend Chart (detail view)
// ---------------------------------------------------------------------------

function TrendChartTooltip({
  active,
  payload,
  label,
  unit,
  timezone,
}: {
  active?: boolean;
  payload?: Array<{
    dataKey?: string | number;
    name?: string;
    value?: number;
    color?: string;
  }>;
  label?: number;
  unit: string;
  timezone: "local" | "utc";
}) {
  if (!active || !payload?.length) return null;
  const labelDate = new Date(label as number);
  const formattedLabel =
    timezone === "utc"
      ? `${labelDate.toLocaleString("en-US", { timeZone: "UTC" })} UTC`
      : labelDate.toLocaleString("en-US");
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-popover-foreground shadow-md">
      <p className="mb-1.5 font-mono text-[11px] text-muted-foreground">{formattedLabel}</p>
      {payload
        .filter((entry) => entry.value != null)
        .map((entry) => (
          <div key={entry.dataKey} className="flex items-center gap-2 text-[12px]">
            <span
              className="inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-foreground">{entry.name}:</span>
            <span className="font-mono font-medium">
              {formatMetricValue(entry.value as number, unit)}
            </span>
          </div>
        ))}
    </div>
  );
}

function TrendChart({
  data,
  unit,
  timezone,
  onClickPoint,
}: {
  data: Array<{
    timestamp: string;
    value: number | null;
    run_id: string;
    version: string;
  }>;
  unit: string;
  timezone: "local" | "utc";
  onClickPoint?: (runId: string) => void;
}) {
  const { merged, versions } = useMemo(() => mergeVersionData(data), [data]);

  if (merged.length === 0) {
    return (
      <div className="flex h-[360px] items-center justify-center">
        <span className="text-sm text-muted-foreground">
          No data for this metric in the selected range
        </span>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={360}>
      <LineChart
        data={merged}
        margin={{ top: 8, right: 12, left: -8, bottom: 0 }}
        onClick={(event) => {
          const innerPayload = (
            event as unknown as {
              activePayload?: Array<{ payload?: Record<string, unknown> }>;
            }
          )?.activePayload?.[0]?.payload;
          if (innerPayload?.run_id && onClickPoint) {
            onClickPoint(String(innerPayload.run_id));
          }
        }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" strokeOpacity={0.5} />
        <XAxis
          dataKey="timestamp"
          type="number"
          scale="time"
          domain={["dataMin", "dataMax"]}
          tickFormatter={(value: number) =>
            new Date(value).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              ...(timezone === "utc" ? { timeZone: "UTC" } : {}),
            })
          }
          stroke="var(--color-muted-foreground)"
          tick={{ fontSize: 11, fontFamily: "var(--font-mono)" }}
          tickLine={false}
          axisLine={false}
          minTickGap={24}
        />
        <YAxis
          tickFormatter={(value: number) => formatMetricValue(value, unit)}
          stroke="var(--color-muted-foreground)"
          tick={{ fontSize: 11, fontFamily: "var(--font-mono)" }}
          tickLine={false}
          axisLine={false}
          width={70}
        />
        <Tooltip content={<TrendChartTooltip unit={unit} timezone={timezone} />} />
        {versions.length > 1 && (
          <Legend wrapperStyle={{ fontSize: "11px", fontFamily: "var(--font-mono)" }} />
        )}
        {versions.map((version, index) => (
          <Line
            key={version}
            type="monotone"
            dataKey={sanitizeKey(version)}
            name={version}
            stroke={CHART_COLORS[index % CHART_COLORS.length]}
            strokeWidth={2}
            dot={{
              r: 3.5,
              cursor: "pointer",
              strokeWidth: 0,
              fill: CHART_COLORS[index % CHART_COLORS.length],
            }}
            activeDot={{
              r: 5,
              strokeWidth: 0,
              fill: CHART_COLORS[index % CHART_COLORS.length],
            }}
            connectNulls
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------------------
// Overview View
// ---------------------------------------------------------------------------

function OverviewView({
  metric,
  timeWindow,
  agg,
  testType,
  setParam,
}: {
  metric: string;
  timeWindow: string;
  agg: string;
  testType: string;
  setParam: (key: string, value: string) => void;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const effectiveTestType = testType === "all" ? undefined : testType;

  // Filter METRICS to hide cryod-only metrics when viewing just CRIU.
  const visibleMetrics = useMemo(
    () => METRICS.filter((entry) => !(entry.cryodOnly && testType === "criu")),
    [testType],
  );
  // If the current metric is hidden by the filter, fall back to the first
  // visible one so the chart doesn't go blank on filter switch.
  const effectiveMetric = visibleMetrics.find((entry) => entry.value === metric)
    ? metric
    : (visibleMetrics[0]?.value ?? metric);
  const metricConfig =
    visibleMetrics.find((entry) => entry.value === effectiveMetric) ??
    visibleMetrics[0] ??
    METRICS[0];

  const overviewParams: Record<string, string> = {
    metric: effectiveMetric,
    window: timeWindow,
    agg,
  };
  if (effectiveTestType) {
    overviewParams.test_type = effectiveTestType;
  }
  const { data: overviewData, isLoading } = useCiOverview(overviewParams);

  const summaries = useMemo(() => groupOverviewRows(overviewData ?? []), [overviewData]);

  return (
    <>
      {/* Controls — compact row */}
      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={effectiveMetric}
          onValueChange={(value) => value && setParam("metric", value)}
        >
          <SelectTrigger className="h-8 w-44 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {visibleMetrics.map((entry) => (
              <SelectItem key={entry.value} value={entry.value}>
                {entry.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="h-5 w-px bg-border" />

        <Select value={testType} onValueChange={(value) => value && setParam("type", value)}>
          <SelectTrigger className="h-8 w-36 text-sm">
            <SelectValue>
              {TEST_TYPE_FILTERS.find((filter) => filter.value === testType)?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {TEST_TYPE_FILTERS.map(({ label, value }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="h-5 w-px bg-border" />

        <PillGroup
          options={AGGREGATIONS}
          value={agg}
          onChange={(value) => setParam("agg", value)}
          disabled={timeWindow === "latest"}
        />

        <div className="h-5 w-px bg-border" />

        <PillGroup
          options={OVERVIEW_WINDOWS}
          value={timeWindow}
          onChange={(value) => setParam("window", value)}
        />
      </div>

      {/* Summary cards */}
      {!isLoading && summaries.length > 0 && (
        <OverviewSummaryCards summaries={summaries} metricUnit={metricConfig.unit} />
      )}

      {/* Table */}
      <OverviewTable
        data={overviewData ?? []}
        metricLabel={metricConfig.label}
        metricUnit={metricConfig.unit}
        isLoading={isLoading}
        onSelectTest={(testName) => {
          const params = new URLSearchParams(searchParams);
          params.set("test", testName);
          navigate(`?${params.toString()}`);
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Detail View
// ---------------------------------------------------------------------------

function DetailView({
  testName,
  metric,
  range,
  customStart,
  customEnd,
  setParam,
  setRange,
  goBack,
}: {
  testName: string;
  metric: string;
  range: string;
  customStart: string;
  customEnd: string;
  setParam: (key: string, value: string) => void;
  setRange: (value: string) => void;
  goBack: () => void;
}) {
  const navigate = useNavigate();
  const { timezone } = useTimezone();
  const metricConfig = METRICS.find((entry) => entry.value === metric) ?? METRICS[0];
  const { display, operation } = parseTestDisplay(testName);
  const isCustomRange = range === "custom" && customStart && customEnd;

  const apiParams: Record<string, string> = { test_name: testName, metric };
  if (isCustomRange) {
    apiParams.start = customStart;
    apiParams.end = customEnd;
  } else {
    apiParams.range = range;
  }

  const { data: trendData, isLoading } = useCiTrends(apiParams);

  return (
    <>
      {/* Back + title */}
      <div className="space-y-1">
        <button
          type="button"
          onClick={goBack}
          className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Overview
        </button>
        <div className="flex items-center gap-2">
          <h2 className="font-mono text-lg font-semibold">{display}</h2>
          {operation === "XR" && (
            <span className="rounded border border-amber-500/20 bg-amber-500/10 px-1.5 py-0.5 font-mono text-[9px] font-semibold text-amber-600 dark:text-amber-400">
              XR
            </span>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={metric} onValueChange={(value) => value && setParam("metric", value)}>
          <SelectTrigger className="h-8 w-44 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {METRICS.map((entry) => (
              <SelectItem key={entry.value} value={entry.value}>
                {entry.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="h-5 w-px bg-border" />

        <PillGroup
          options={[...DETAIL_RANGES, { label: "Custom", value: "custom" }]}
          value={range}
          onChange={(value) => {
            if (value === "custom") {
              const now = new Date();
              const twoWeeksAgo = new Date(now.getTime() - 14 * 86400000);
              setRange("custom");
              setParam("start", twoWeeksAgo.toISOString().slice(0, 10));
              setParam("end", now.toISOString().slice(0, 10));
            } else {
              setRange(value);
            }
          }}
        />

        {range === "custom" && (
          <>
            <div className="h-5 w-px bg-border" />
            <DatePicker
              value={customStart ? new Date(`${customStart}T00:00:00Z`) : undefined}
              onChange={(date) => setParam("start", date ? date.toISOString().slice(0, 10) : "")}
              placeholder="Start"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <DatePicker
              value={customEnd ? new Date(`${customEnd}T00:00:00Z`) : undefined}
              onChange={(date) => setParam("end", date ? date.toISOString().slice(0, 10) : "")}
              placeholder="End"
            />
          </>
        )}
      </div>

      {/* Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            {metricConfig.label}
            <span className="ml-2 font-mono text-sm font-normal text-muted-foreground">
              {testName}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-[360px] w-full" />
          ) : (
            <TrendChart
              data={trendData ?? []}
              unit={metricConfig.unit}
              timezone={timezone}
              onClickPoint={(runId) => navigate(`/ci/${runId}`)}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CiTrendsPage() {
  usePageTitle("CI Trends");
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTest = searchParams.get("test") ?? "";
  const selectedMetric = searchParams.get("metric") ?? "total_restore_ms";
  const selectedWindow = searchParams.get("window") ?? "30d";
  const selectedAgg = searchParams.get("agg") ?? "mean";
  const selectedRange = searchParams.get("range") ?? "30d";
  const customStart = searchParams.get("start") ?? "";
  const customEnd = searchParams.get("end") ?? "";
  const selectedTestType = searchParams.get("type") ?? "all";

  const setParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const setRange = useCallback(
    (value: string) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        next.set("range", value);
        if (value !== "custom") {
          next.delete("start");
          next.delete("end");
        }
        return next;
      });
    },
    [setSearchParams],
  );

  const goBackToOverview = useCallback(() => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete("test");
      next.delete("range");
      next.delete("start");
      next.delete("end");
      return next;
    });
  }, [setSearchParams]);

  return (
    <div className="mx-auto max-w-screen-xl space-y-4 p-6">
      {!selectedTest && (
        <div>
          <h1 className="text-2xl font-semibold">CI Trends</h1>
          <p className="text-sm text-muted-foreground">Performance overview across all CI tests</p>
        </div>
      )}

      {selectedTest ? (
        <DetailView
          testName={selectedTest}
          metric={selectedMetric}
          range={selectedRange}
          customStart={customStart}
          customEnd={customEnd}
          setParam={setParam}
          setRange={setRange}
          goBack={goBackToOverview}
        />
      ) : (
        <OverviewView
          metric={selectedMetric}
          timeWindow={selectedWindow}
          agg={selectedAgg}
          testType={selectedTestType}
          setParam={setParam}
        />
      )}
    </div>
  );
}
